import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, LogOut, FileDown, RefreshCw, ClipboardList, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AGDCourseRow } from "@/components/admin/AGDCourseRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

const PROVIDER_FIELDS = [
  ["provider_id", "PACE Provider ID Number"],
  ["organization_name", "Official organization name of CDE program provider"],
  ["contact_name_title", "Contact person name and title"],
  ["email", "E-mail address"],
  ["street_address", "Street address"],
  ["city_state_zip", "City, State and Zip"],
  ["telephone", "Telephone number"],
  ["fax", "Fax number"],
  ["website", "Web site"],
];
const EMPTY_COURSE = { title: "", dates_offered: "", location: "Online", intended_audience: "G, S", num_students: "", num_instructors: "1", method_of_delivery: "SI", prerequisite: "No", offered_with_other_provider: "No", financial_support: "No", credit_hours: "" };

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function AdminAGDReport({ logout }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("session_token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [provider, setProvider] = useState({});
  const [affirm, setAffirm] = useState(true);
  const [courses, setCourses] = useState([EMPTY_COURSE, EMPTY_COURSE, EMPTY_COURSE]);
  const [suggested, setSuggested] = useState([]);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);

  const loadHistory = useCallback(
    () => axios.get(`${API}/admin/agd-report/history`, { headers }).then((r) => setHistory(r.data)).catch(() => {}),
    [headers]
  );

  useEffect(() => {
    axios.get(`${API}/admin/agd-report/prefill`, { headers }).then((res) => {
      setProvider(res.data.provider || {});
      setAffirm(res.data.affirm ?? true);
      setReportYear(res.data.report_year);
      setSuggested(res.data.suggested_courses);
      const filled = [...res.data.suggested_courses.map((c) => ({ ...EMPTY_COURSE, ...c }))];
      while (filled.length < 3) filled.push(EMPTY_COURSE);
      setCourses(filled.slice(0, 3));
    }).catch(() => toast.error("Failed to load report data"));
    loadHistory();
  }, [headers, loadHistory]);

  const applySuggested = () => {
    const filled = suggested.map((c) => ({ ...EMPTY_COURSE, ...c }));
    while (filled.length < 3) filled.push(EMPTY_COURSE);
    setCourses(filled.slice(0, 3));
    toast.success("Course rows refilled from platform data");
  };

  const handleGenerate = async () => {
    if (!provider.provider_id?.trim() || !provider.organization_name?.trim()) {
      toast.error("PACE Provider ID and organization name are required");
      return;
    }
    if (!courses.some((c) => c.title?.trim())) {
      toast.error("Enter at least one course");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/agd-report/generate`, { report_year: reportYear, provider, affirm, courses, notes }, { headers, responseType: "blob" });
      downloadBlob(res.data, `AGD_PACE_Annual_Report_${reportYear}.pdf`);
      toast.success("Report PDF generated and saved to history");
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const downloadPast = async (r) => {
    try {
      const res = await axios.get(`${API}/admin/agd-report/${r.id}/download`, { headers, responseType: "blob" });
      downloadBlob(res.data, `AGD_PACE_Annual_Report_${r.report_year}.pdf`);
    } catch {
      toast.error("Download failed");
    }
  };

  const deletePast = async (r) => {
    if (!window.confirm(`Delete the ${r.report_year} report from history?`)) return;
    await axios.delete(`${API}/admin/agd-report/${r.id}`, { headers }).catch(() => toast.error("Delete failed"));
    loadHistory();
  };

  const loadPast = (r) => {
    setProvider(r.provider);
    setAffirm(r.affirm);
    setCourses([...r.courses, EMPTY_COURSE, EMPTY_COURSE, EMPTY_COURSE].slice(0, 3));
    setReportYear(r.report_year);
    setNotes(r.notes || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(`Loaded ${r.report_year} report into the form`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="admin-agd-report-page">
      <header className="bg-white shadow-md border-b-4" style={{ borderColor: "#9E3639" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-8 sm:h-12" />
            <div>
              <h1 className="text-base sm:text-2xl font-bold" style={{ color: "#092036" }}>AGD PACE Annual Report</h1>
              <p className="text-xs sm:text-sm text-slate-600 hidden sm:block">Fill the official form from your platform data and download the completed PDF</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin")} className="gap-2" data-testid="agd-back-btn"><ArrowLeft className="w-4 h-4" /> Admin</Button>
            <Button variant="outline" size="sm" onClick={logout} className="gap-2"><LogOut className="w-4 h-4" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <section className="bg-white rounded-lg border p-5 space-y-4" data-testid="agd-provider-section">
          <div className="flex items-center justify-between">
            <h2 className="text-base md:text-lg font-semibold" style={{ color: "#092036" }}>Provider Information</h2>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-600">Report year</Label>
              <Input type="number" value={reportYear} onChange={(e) => setReportYear(Number(e.target.value))} className="w-24" data-testid="agd-report-year" />
            </div>
          </div>
          <p className="text-xs text-slate-500">Saved automatically when you generate a report, so next year this is already filled in.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {PROVIDER_FIELDS.map(([k, label]) => (
              <div key={k} className={k === "organization_name" || k === "contact_name_title" ? "md:col-span-2" : ""}>
                <Label className="text-xs text-slate-600">{label}</Label>
                <Input value={provider[k] || ""} onChange={(e) => setProvider((p) => ({ ...p, [k]: e.target.value }))} data-testid={`agd-provider-${k}`} />
              </div>
            ))}
          </div>
          <label className="flex items-start gap-3 text-sm p-3 rounded-lg bg-slate-50 border cursor-pointer" data-testid="agd-affirm-label">
            <input type="checkbox" checked={affirm} onChange={(e) => setAffirm(e.target.checked)} className="mt-1" data-testid="agd-affirm-checkbox" />
            <span className="text-slate-700">
              I affirm that, in accordance with PACE Standard XI: Course Records, I am submitting continuing education credits earned by AGD members at my courses to the AGD via the AGD online roster within 30 days of course completion.
            </span>
          </label>
        </section>

        <section className="space-y-3" data-testid="agd-courses-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-base md:text-lg font-semibold" style={{ color: "#092036" }}>Three Most Recent Courses (last 12 months)</h2>
              <p className="text-xs text-slate-500">Pre-filled from certificates issued on the platform — student counts are unique students who completed each course. Edit anything before generating.</p>
            </div>
            <Button variant="outline" size="sm" onClick={applySuggested} className="gap-2" data-testid="agd-refill-btn"><RefreshCw className="w-4 h-4" /> Refill from platform data</Button>
          </div>
          {courses.map((c, i) => (
            <AGDCourseRow key={i} index={i} course={c} onChange={(next) => setCourses((cs) => cs.map((x, j) => (j === i ? next : x)))} />
          ))}
          <p className="text-xs text-slate-500">Audience: G=General dentist, S=Specialist, DH=Hygienist, DA=Assistant, DL=Lab technician · Method: L=Lecture, PA=Participation, PR=Protocol, SI=Self-instruction, W=Live webinar</p>
        </section>

        <section className="bg-white rounded-lg border p-5 space-y-3" data-testid="agd-attachments-section">
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2" style={{ color: "#092036" }}><ClipboardList className="w-5 h-5" /> Annual Documentation to attach when you submit</h2>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>A copy of your most recent CE Planning Committee meeting minutes</li>
            <li>A sample publicity from the past twelve months</li>
            <li>A completed commercial sponsor agreement (if applicable)</li>
          </ul>
          <div>
            <Label className="text-xs text-slate-600">Private notes for this year (kept in history, not printed)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. Submitted via AGD portal on 10/15, confirmation #..." data-testid="agd-notes" />
          </div>
        </section>

        <div className="flex justify-end">
          <Button onClick={handleGenerate} disabled={generating} className="gap-2 bg-[#102862] hover:bg-[#1a3a7a] text-white px-6 py-5 text-base" data-testid="agd-generate-btn">
            <FileDown className="w-5 h-5" /> {generating ? "Generating..." : "Generate completed PDF"}
          </Button>
        </div>

        <section className="bg-white rounded-lg border p-5" data-testid="agd-history-section">
          <h2 className="text-base md:text-lg font-semibold mb-3" style={{ color: "#092036" }}>Past Reports</h2>
          {history.length === 0 ? <p className="text-sm text-slate-500" data-testid="agd-history-empty">No reports generated yet.</p> : (
            <div className="divide-y">
              {history.map((r) => (
                <div key={r.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm" data-testid={`agd-history-${r.id}`}>
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: "#092036" }}>{r.report_year} Annual Report</p>
                    <p className="text-xs text-slate-500">Generated {new Date(r.generated_at).toLocaleString()} by {r.generated_by} · {r.courses.filter((c) => c.title).length} course(s){r.notes ? ` · ${r.notes}` : ""}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => loadPast(r)} data-testid={`agd-history-load-${r.id}`}>Load into form</Button>
                    <Button variant="outline" size="sm" onClick={() => downloadPast(r)} className="gap-1" data-testid={`agd-history-download-${r.id}`}><FileDown className="w-4 h-4" /> PDF</Button>
                    <Button variant="ghost" size="sm" onClick={() => deletePast(r)} data-testid={`agd-history-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
